import type { Integration as SharedIntegration } from '@gofranz/common-components';
import { IntegrationsList, useLanguageAwareRouting } from '@gofranz/common-components';
import { Box, Stack } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../../components/Common/PageHeader';
import { useRustyState } from '../../../state';

const PAGE_SIZE = 20;

/**
 * Lists the user's outbound integrations.
 *
 * The list / form / delivery components live in `@gofranz/common-components`
 * and are pure presentational pieces. This page owns:
 *   - paginated fetch + refresh after mutations
 *   - delete confirmation modal
 *   - navigation to create / edit / deliveries pages
 */
export function AccountIntegrationsStartPage() {
  const { t } = useTranslation();
  const { api } = useRustyState.getState();
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();

  const [integrations, setIntegrations] = useState<SharedIntegration[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      try {
        const offset = (targetPage - 1) * PAGE_SIZE;
        const res = await api.getIntegrations({ limit: PAGE_SIZE, offset });
        // The backend response and the shared component type both use the
        // shape {id, user_id, title, kind, config, created_at, updated_at}.
        // The shared type optionally carries `hmac_secret_b64`; we don't get
        // that back on list reads — leaving it undefined matches the
        // Integration interface contract.
        setIntegrations(res.data as SharedIntegration[]);
        setTotal(res.total);
      } catch (e) {
        console.error('Failed to load integrations', e);
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  useEffect(() => {
    load(page);
  }, [load, page]);

  const handleCreate = useCallback(() => {
    nav(createLanguageURL('/account/integrations/create'));
  }, [nav, createLanguageURL]);

  const handleEdit = useCallback(
    (integration: SharedIntegration) => {
      nav(createLanguageURL(`/account/integrations/${integration.id}`));
    },
    [nav, createLanguageURL]
  );

  const handleViewDeliveries = useCallback(
    (integration: SharedIntegration) => {
      nav(createLanguageURL(`/account/integrations/${integration.id}/deliveries`));
    },
    [nav, createLanguageURL]
  );

  const handleDelete = useCallback(
    (integration: SharedIntegration) => {
      modals.openConfirmModal({
        title: t('formshive.integrations.deleteModal.title'),
        children: t('formshive.integrations.deleteModal.body', { title: integration.title }),
        labels: {
          confirm: t('integrations.actions.delete'),
          cancel: t('integrations.actions.cancel'),
        },
        confirmProps: { color: 'red' },
        onConfirm: async () => {
          try {
            await api.deleteIntegration(integration.id);
            notifications.show({
              color: 'green',
              title: t('formshive.integrations.deleteSuccess.title'),
              message: t('formshive.integrations.deleteSuccess.body'),
            });
            await load(page);
          } catch (e) {
            console.error('Failed to delete integration', e);
          }
        },
      });
    },
    [api, t, load, page]
  );

  const subtitle = useMemo(() => t('formshive.integrations.startPage.subtitle'), [t]);

  return (
    <Stack gap="xl">
      <Box>
        <PageHeader title={t('formshive.integrations.startPage.title')} subtitle={subtitle} />
        <IntegrationsList
          integrations={integrations}
          loading={loading}
          totalCount={total}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          onCreate={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onViewDeliveries={handleViewDeliveries}
        />
      </Box>
    </Stack>
  );
}
